<template>
  <div>
    <!-- `semi-transparent-card light-shadow` are the app's own classes; scoped rules below add only layout. -->
    <div class="documents-toolbar semi-transparent-card light-shadow">
      <!-- Grouped so the mobile breakpoint can lay out each row deliberately, without
           disturbing the single-row flex layout on desktop (`display: contents` below). -->
      <div class="toolbar-tabs">
        <button
          type="button"
          class="btn btn-sm rounded-pill toolbar-tab"
          :class="tab === 'folders' ? 'btn-theme' : 'toolbar-tab-idle'"
          @click="switchTab('folders')"
        >
          All folders {{ folders.meta.total }}
        </button>
        <button
          type="button"
          class="btn btn-sm rounded-pill toolbar-tab ml-2"
          :class="tab === 'shared' ? 'btn-theme' : 'toolbar-tab-idle'"
          @click="switchTab('shared')"
        >
          Shared with me {{ sharedWithMe.meta.total }}
        </button>
      </div>

      <div class="toolbar-search">
        <i class="fas fa-search"></i>
        <input
          type="text"
          :placeholder="
            tab === 'folders' ? 'Search folders and files' : 'Search documents'
          "
          v-model="searchTerm"
          @input="onSearchInput"
        />
        <button
          v-if="searchTerm"
          type="button"
          class="clear-search"
          title="Clear search"
          @click="clearSearch"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="toolbar-actions">
        <div
          v-if="tab === 'folders' && emptyFolderCount"
          class="custom-control custom-checkbox hide-empty-control mr-3"
          v-tooltip.top="
            query.hideEmpty
              ? 'Showing only folders with documents'
              : 'Hide the folders holding nothing'
          "
        >
          <input
            type="checkbox"
            class="custom-control-input"
            id="documentsHideEmpty"
            :checked="query.hideEmpty"
            @change="toggleHideEmpty"
          />
          <label class="custom-control-label" for="documentsHideEmpty">
            Hide empty ({{ emptyFolderCount }})
          </label>
        </div>

        <button
          v-if="tab === 'folders' && canCreate"
          class="btn btn-sm btn-theme rounded-pill px-4 new-folder-btn"
          @click="promptNewFolder"
        >
          New folder
        </button>
      </div>
    </div>

    <alert v-if="isLoadingTab" class="light-shadow" />

    <!-- All folders -->
    <div
      v-if="!isLoadingTab && tab === 'folders'"
      class="documents-card theme-card"
    >
      <alert v-if="!folders.data.length" class="m-4">No folders yet</alert>

      <div v-else class="table-responsive">
        <node-table
          :rows="folders.data"
          :query="query"
          :subject-user-id="subjectUserId"
          @sort="sortBy"
          @open-folder="$emit('open-folder', $event)"
          @share-folder="$emit('share-folder', $event)"
          @move-folder="$emit('move-folder', $event)"
          @show-audience="$emit('show-audience', $event)"
          @changed="reload"
        />
      </div>

      <div class="documents-footer">
        <span
          >{{ rangeLabel(folders.meta) }} · folder contents load one folder at a
          time</span
        >
      </div>

      <div class="px-3 pb-3" v-if="folders.meta.last_page > 1">
        <pagination
          :current-page="folders.meta.current_page"
          :total-pages="folders.meta.last_page"
          :total="folders.meta.total"
          @page-changed="goToPage"
        />
      </div>
    </div>

    <!-- Shared with me: documents, not folders -->
    <div
      v-if="!isLoadingTab && tab === 'shared'"
      class="documents-card theme-card"
    >
      <alert v-if="!sharedWithMe.data.length" class="m-4"
        >Nothing shared with you</alert
      >

      <div v-else class="table-responsive">
        <!-- Loose documents only: no folders, so the folder-only columns would be blank on
             every row. Same three this tab has always shown. -->
        <node-table
          :rows="sharedWithMe.data"
          :query="sharedQuery"
          :subject-user-id="subjectUserId"
          :columns="['name', 'added', 'count']"
          @sort="sortSharedBy"
          @changed="reload"
        />
      </div>

      <div class="documents-footer">
        <span>{{ rangeLabel(sharedWithMe.meta) }}</span>
      </div>

      <div class="px-3 pb-3" v-if="sharedWithMe.meta.last_page > 1">
        <pagination
          :current-page="sharedWithMe.meta.current_page"
          :total-pages="sharedWithMe.meta.last_page"
          :total="sharedWithMe.meta.total"
          @page-changed="goToSharedPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapActions, mapState } from "vuex";
import {
  SEARCH_DEBOUNCE_MS,
  promptNewFolder,
  toggleSort,
} from "./useDocumentsArea";

export default {
  name: "FolderTable",
  props: {
    subjectUserId: { type: Number, default: null },
    subjectKind: { type: String, default: "client" },
    canCreate: { type: Boolean, default: false },
  },
  emits: ["open-folder", "share-folder", "show-audience", "move-folder"],
  data() {
    return {
      tab: "folders",
      searchTerm: this.$store.state.folders.query.search || "",
    };
  },
  computed: {
    ...mapState({
      folders: (state) => state.folders.folders,
      sharedWithMe: (state) => state.folders.sharedWithMe,
      query: (state) => state.folders.query,
      sharedQuery: (state) => state.folders.sharedQuery,
      isLoading: (state) => state.folders.isLoading,
      isLoadingShared: (state) => state.folders.isLoadingShared,
    }),

    /** The spinner follows the tab you are on, not whichever request happens to be in flight. */
    isLoadingTab() {
      return this.tab === "folders" ? this.isLoading : this.isLoadingShared;
    },

    /** Counted by the API over the unfiltered scope, so it doesn't collapse to zero once the filter is on. */
    emptyFolderCount() {
      return (this.folders.meta && this.folders.meta.empty_folders) || 0;
    },
  },
  created() {
    // Per-instance rather than module scope, so two mounted tabs cannot share a timer.
    this.runSearch = debounce(() => {
      this.patch(this.tab === "folders" ? "query" : "sharedQuery", {
        search: this.searchTerm,
        page: 1,
      });
      this.reload();
    }, SEARCH_DEBOUNCE_MS);
  },
  beforeUnmount() {
    this.runSearch.cancel();
  },
  methods: {
    ...mapActions({
      get: "folders/get",
      getSharedWithMe: "folders/getSharedWithMe",
      save: "folders/save",
    }),

    patch(key, patchValue) {
      this.$store.commit("folders/PATCH_QUERY", { key, patch: patchValue });
    },

    reload() {
      return this.tab === "folders"
        ? this.get(this.subjectUserId)
        : this.getSharedWithMe(this.subjectUserId);
    },

    clearSearch() {
      this.runSearch.cancel();
      this.searchTerm = "";
      this.patch(this.tab === "folders" ? "query" : "sharedQuery", {
        search: "",
        page: 1,
      });
      this.reload();
    },

    switchTab(tab) {
      if (this.tab === tab) {
        return;
      }
      this.runSearch.cancel();
      this.tab = tab;
      this.searchTerm = "";
      this.patch(tab === "folders" ? "query" : "sharedQuery", {
        search: "",
        page: 1,
      });
      this.reload();
    },

    onSearchInput() {
      this.runSearch();
    },

    toggleHideEmpty() {
      this.patch("query", { hideEmpty: !this.query.hideEmpty, page: 1 });
      this.get(this.subjectUserId);
    },

    sortBy(column) {
      this.patch("query", { ...toggleSort(this.query, column), page: 1 });
      this.get(this.subjectUserId);
    },

    sortSharedBy(column) {
      this.patch("sharedQuery", {
        ...toggleSort(this.sharedQuery, column),
        page: 1,
      });
      this.getSharedWithMe(this.subjectUserId);
    },

    goToPage(page) {
      this.patch("query", { page });
      this.get(this.subjectUserId);
    },

    goToSharedPage(page) {
      this.patch("sharedQuery", { page });
      this.getSharedWithMe(this.subjectUserId);
    },

    /** "Showing 1–25 of 26" — the footer line, computed from the paginator's own meta. */
    rangeLabel(meta) {
      if (!meta.total) {
        return "Nothing to show";
      }

      const from = (meta.current_page - 1) * meta.per_page + 1;
      const to = Math.min(meta.current_page * meta.per_page, meta.total);

      return `Showing ${from}–${to} of ${meta.total}`;
    },

    promptNewFolder() {
      promptNewFolder(this, {
        subjectUserId: this.subjectUserId,
        onCreated: () => this.get(this.subjectUserId),
      });
    },
  },
};
</script>

<style scoped lang="scss">
/* ---- toolbar --------------------------------------------------------------- */
.documents-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

/* On desktop these are invisible wrappers — children lay out exactly as if the toolbar were
   still one flat flex row. The mobile breakpoint below turns each into its own deliberate row. */
.toolbar-tabs,
.toolbar-actions {
  display: contents;
}

.toolbar-tab {
  font-weight: 500;
  white-space: nowrap;
  padding: 0.35rem 1.1rem;
}

/* Solid white so idle pills read as raised ON the translucent bar, not as holes cut into it. */
.toolbar-tab-idle {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: var(--main-text-color, #222222);

  &:hover {
    background: rgba(255, 255, 255, 0.75);
  }
}

/* Lines up with the pill buttons beside it — the Bootstrap checkbox/label pair is inline by
   default but sits a little low against a `btn-sm` baseline. */
.hide-empty-control {
  display: flex;
  align-items: center;
  white-space: nowrap;

  .custom-control-label {
    cursor: pointer;
  }
}

.toolbar-search {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 1rem;
  background: #ffffff;
  border-radius: 24px;
  padding: 0.4rem 1rem;

  /* Fixed greys ON PURPOSE: the search box is always opaque white, unlike the rest of this file. */
  i {
    color: #9aa0a6;
    margin-right: 0.6rem;
    flex-shrink: 0;
  }

  input {
    border: 0;
    background: transparent;
    outline: none;
    width: 100%;
    font-size: 0.95rem;
    color: var(--main-text-color, #222222);

    &::placeholder {
      color: #9aa0a6;
    }
  }

  .clear-search i {
    margin-right: 0;
  }
}

.clear-search {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 0;
  background: none;
  color: #9aa0a6;
  line-height: 1;
  padding: 0;
}

.clear-search:hover {
  background: rgba(0, 0, 0, 0.06);
  color: var(--main-text-color, #222222);
}

/* ---- table card -------------------------------------------------------------
   Surface comes from the global `.theme-card`; only corner rounding belongs here. */
.documents-card {
  border-radius: 14px;
  overflow: hidden;
}

/* Touch devices have no hover to reveal anything, so the actions stay put. */
@media (hover: none) {
  .row-actions {
    opacity: 1;
    pointer-events: auto;
  }

  .row-chevron {
    opacity: 0;
  }
}

/* ---- footer --------------------------------------------------------------- */
.documents-footer {
  padding: 0.6rem 1rem;
  text-align: right;
  font-size: 0.8rem;
  color: var(--main-text-color);
  opacity: 0.6;
}

@media (max-width: 1024px) {
  /* Stacked into three rows — tabs, search, then "Hide empty" and "New folder" together. The
     tabs stay the pills they are on desktop; only the layout changes with the width. */
  .documents-toolbar {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    border-radius: 16px !important;
  }

  .toolbar-tabs {
    display: flex;
    margin-bottom: 0.75rem;
  }

  .toolbar-search {
    margin: 0 0 0.75rem 0;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .toolbar-actions .hide-empty-control.mr-3 {
    margin: 0;
  }

  .toolbar-actions .toolbar-tab,
  .new-folder-btn {
    padding-top: 0.55rem;
    padding-bottom: 0.55rem;
  }

  .documents-table td,
  .documents-table th {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}
</style>
